const g = typeof globalThis !== 'undefined' ? globalThis : (typeof window !== 'undefined' ? window : global);

if (!g.__STEM_CODE_GEN_STATE__) {
    g.__STEM_CODE_GEN_STATE__ = {};
}
const state = g.__STEM_CODE_GEN_STATE__;

// Ensure all properties are initialized on the global state object
if (typeof state.defineCode !== 'string') state.defineCode = '';
if (typeof state.bodyCode !== 'string') state.bodyCode = '';
if (typeof state.setupCode !== 'string') state.setupCode = '';
if (typeof state.loopCode !== 'string') state.loopCode = '';
if (!state.declaredVariables || !(state.declaredVariables instanceof Set)) {
    state.declaredVariables = new Set();
}
if (typeof state.isRuntimeStopped !== 'boolean') {
    state.isRuntimeStopped = true;
}
state.lastThread = null;

// Helper to remove exact duplicate lines (for headers, defines, setup, body functions, etc.)
function deduplicateLines(code) {
    const lines = (code || '').trim().split('\n');
    const seen = new Set();
    const result = [];
    for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        if (!seen.has(trimmed)) {
            seen.add(trimmed);
            result.push(trimmed);
        }
    }
    return result.join('\n');
}

const codeGeneration = {
    // Called when the VM runtime or a project/script starts running
    onRunStart() {
        console.log('🚀 [codeGeneration] Runtime/Script execution STARTED. Clearing loopCode for fresh accumulation.');
        state.isRuntimeStopped = false;
        state.loopCode = '';
    },

    // Called when the VM runtime finishes running all threads
    onRunStop() {
        console.log('🛑 [codeGeneration] Runtime/Script execution STOPPED. Setting isRuntimeStopped flag = true.');
        state.isRuntimeStopped = true;
    },

    // Method to accumulate code from different extensions
    accumulateCode(define, body, setup, loop, util) {
        const thread = util && util.thread;

        // If runtime previously stopped, this is a fresh run: unflag and clear loopCode
        if (state.isRuntimeStopped) {
            console.log('▶️ [codeGeneration] New run detected after runtime stop. Unflagging isRuntimeStopped and clearing loopCode.');
            state.isRuntimeStopped = false;
            state.loopCode = '';
            state.lastThread = thread;
        } else if (thread && state.lastThread && state.lastThread !== thread) {
            // New thread instance detected
            console.log('🔄 [codeGeneration] Thread instance changed. Resetting loopCode.');
            state.lastThread = thread;
            state.loopCode = '';
        } else if (thread) {
            state.lastThread = thread;
        }

        if (define && define.trim()) {
            state.defineCode = (state.defineCode || '') + define.trim() + '\n';
        }
        if (body && body.trim()) {
            state.bodyCode = (state.bodyCode || '') + body.trim() + '\n';
        }
        if (setup && setup.trim()) {
            state.setupCode = (state.setupCode || '') + setup.trim() + '\n';
        }
        if (loop && loop.trim()) {
            state.loopCode = (state.loopCode || '') + loop.trim() + '\n';
        }

        console.log('📥 [codeGeneration] accumulateCode called:', {
            define: (define || '').trim(),
            body: (body || '').trim(),
            setup: (setup || '').trim(),
            loop: (loop || '').trim(),
            currentLoopCode: (state.loopCode || '').trim()
        });
    },

    setSerialBaudRate(rate) {
        state.serialBaudRate = parseInt(rate, 10);
    },

    getSerialBaudRate() {
        return state.serialBaudRate;
    },

    declareVariable(name) {
        const sanitized = name.replace(/[^a-zA-Z0-9_]/g, '_');
        let safeName = sanitized;
        if (/^[0-9]/.test(safeName)) {
            safeName = '_' + safeName;
        }

        if (!state.declaredVariables || !(state.declaredVariables instanceof Set)) {
            state.declaredVariables = new Set();
        }

        if (!state.declaredVariables.has(safeName)) {
            state.declaredVariables.add(safeName);
            state.defineCode = (state.defineCode || '') + `float ${safeName} = 0;\n`;
        }
        return safeName;
    },

    clearLoopCode() {
        state.loopCode = '';
        console.log('[codeGeneration] clearLoopCode called.');
    },

    // Method to generate the final combined code
    generateFinalCode() {
        console.log('[codeGeneration] generateFinalCode called.');

        // Deduplicate variable declarations & includes
        const lines = (state.defineCode || '').trim().split('\n');
        const seenVars = new Map();

        for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;

            const varMatch = trimmed.match(
                /^(int|float|char|long|double|bool|String|unsigned\s+int|unsigned\s+long)\s+(\w+)/
            );

            if (varMatch) {
                const varName = varMatch[2];
                const hasInit = trimmed.includes('=');

                if (!seenVars.has(varName)) {
                    seenVars.set(varName, trimmed);
                } else if (hasInit && !seenVars.get(varName).includes('=')) {
                    seenVars.set(varName, trimmed);
                }
            } else {
                if (!seenVars.has(trimmed)) {
                    seenVars.set(trimmed, trimmed);
                }
            }
        }

        const cleanDefineCode = Array.from(seenVars.values()).join('\n');
        const cleanSetup = deduplicateLines(state.setupCode || '');
        const cleanLoop = (state.loopCode || '').trim();
        const cleanBody = deduplicateLines(state.bodyCode || '');

        const result = `
        ${cleanDefineCode}
        void setup() {
            ${cleanSetup}
        }
        void loop() {
            ${cleanLoop}
        }
        ${cleanBody}
    `;
        return result;
    },

    resetCode() {
        state.defineCode = '';
        state.setupCode = '';
        state.loopCode = '';
        state.bodyCode = '';
        if (state.declaredVariables && typeof state.declaredVariables.clear === 'function') {
            state.declaredVariables.clear();
        } else {
            state.declaredVariables = new Set();
        }
        state.isRuntimeStopped = true;
        state.lastThread = null;
        console.log('[codeGeneration] resetCode called.');
    }
};

module.exports = codeGeneration;